<template>
    <div class="targets-pdf">
        <button
            type="button"
            class="btn btn--secondary"
            @click="download"
            :disabled="exporting || !canExport"
        >
            <span class="material-symbols-outlined">download</span>
            <span v-if="exporting">Preparing PDF...</span>
            <span v-else>Download PDF</span>
        </button>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { jsPDF } from 'jspdf';
import { EESA_SCOPE_NOTE } from '@/utils/assessment-notes';
import {
    noTargetsIndicated,
    NO_TARGETS_HEADING,
    noTargetsPdfLines,
} from '@/utils/assessment-targets';

const props = defineProps({
    assessmentId: { type: Number, required: true },
    assessment: { type: Object, default: null },
    targets: { type: Array, required: true },
});

const exporting = ref(false);

const learnerName = computed(() => {
    const f = props.assessment?.learner?.firstname;
    const l = props.assessment?.learner?.lastname;
    return [f, l].filter(Boolean).join(' ');
});

const preferences = computed(() => {
    const raw = props.assessment?.note?.preferences_list;
    if (!raw) return {};
    try { return JSON.parse(raw); } catch { return {}; }
});

const groupScoresPct = computed(() => {
    const raw = props.assessment?.result?.group_scores || {};
    const out = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    for (let i = 1; i <= 5; i++) {
        const g = raw[i];
        if (g?.total > 0) out[i] = (g.score_one / g.total) * 100;
    }
    return out;
});

const recommendedGroup = computed(() => {
    const s = groupScoresPct.value;
    if (s[1] < 80) return 1;
    for (let i = 2; i <= 5; i++) {
        if (s[i - 1] < 80) return i - 1;
        if (s[i] < 80) return i;
    }
    return 5;
});

const recommendedGroupTitle = computed(() => ({
    1: 'Foundational single sounds',
    2: 'Two-syllable combinations',
    3: 'Longer syllable strings',
    4: 'Multi-word combinations',
    5: 'Complex phrases',
}[recommendedGroup.value]));

const maxMetrics = computed(() => {
    const averages = props.assessment?.result?.scv_averages || {};
    const vals = Object.values(averages);
    if (!vals.length) return { syllable: 0, consonant: 0, vowel: 0 };
    const max = (key) => Math.max(0, ...vals.map(v => parseFloat(v?.[key]) || 0));
    return {
        syllable: max('syllable'),
        consonant: max('consonant'),
        vowel: max('vowel'),
    };
});

const validTargets = computed(() =>
    props.targets.filter(t => t.header && t.header.trim())
);

const hasTargets = computed(() => validTargets.value.length > 0);

const noTargets = computed(() => noTargetsIndicated(groupScoresPct.value));

// With no echoic targets indicated the PDF is still worth having: it carries the
// Guide's alternative instead of a shaping ladder.
const canExport = computed(() => hasTargets.value || noTargets.value);

const stepLabel = (input) => {
    const key = Object.keys(input)[0];
    if (key === 'end') return 'End';
    if (key === 'start') return 'Start';
    return '...';
};

const stepValue = (input) => {
    const key = Object.keys(input)[0];
    return input[key] || '—';
};

const safeFilename = () => {
    const base = learnerName.value ? learnerName.value.replace(/[^a-z0-9]+/gi, '_') : 'learner';
    const date = new Date().toISOString().slice(0, 10);
    return `ProgramTargets_${base}_${date}.pdf`;
};

// Brand colors
const COLOR_PRIMARY = [114, 92, 255];      // #725CFF
const COLOR_PRIMARY_LIGHT = [247, 246, 255]; // #f7f6ff
const COLOR_SUCCESS = [54, 194, 122];      // #36C27A
const COLOR_TEXT = [34, 34, 34];           // #222
const COLOR_MUTED = [120, 120, 120];
const COLOR_LINE = [230, 230, 230];

const download = async () => {
    if (!canExport.value) return;
    exporting.value = true;
    try {
        const doc = new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
        const pageW = doc.internal.pageSize.getWidth();
        const pageH = doc.internal.pageSize.getHeight();
        const margin = 36;
        const contentW = pageW - margin * 2;
        let y = margin;

        const today = new Date().toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
        });

        const ensureSpace = (needed) => {
            if (y + needed > pageH - margin) {
                doc.addPage();
                y = margin;
            }
        };

        // === Header ===
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(22);
        doc.setTextColor(...COLOR_TEXT);
        doc.text('Program Targets', margin, y + 18);

        doc.setFontSize(9);
        doc.setTextColor(...COLOR_PRIMARY);
        doc.text('EESAPP', pageW - margin, y + 18, { align: 'right' });

        y += 28;

        // Purple underline
        doc.setDrawColor(...COLOR_PRIMARY);
        doc.setLineWidth(1.2);
        doc.line(margin, y, pageW - margin, y);
        y += 12;

        // Meta row
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...COLOR_MUTED);
        const metaParts = [
            `Learner: ${learnerName.value || '—'}`,
            `Assessment #${props.assessmentId}`,
            `Generated: ${today}`,
        ];
        doc.text(metaParts.join('   •   '), margin, y);
        y += 16;

        // Standing note: what the EESA does and does not measure
        const scopeLines = doc.splitTextToSize(EESA_SCOPE_NOTE, contentW);
        doc.text(scopeLines, margin, y);
        y += scopeLines.length * 11 + 10;

        // === Recommendation hero ===
        if (recommendedGroup.value) {
            const heroH = 80;
            ensureSpace(heroH + 6);
            // Left accent bar
            doc.setFillColor(...COLOR_PRIMARY);
            doc.rect(margin, y, 4, heroH, 'F');
            // Hero background
            doc.setFillColor(...COLOR_PRIMARY_LIGHT);
            doc.rect(margin + 4, y, contentW - 4, heroH, 'F');

            // Label
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(8);
            doc.setTextColor(...COLOR_PRIMARY);
            doc.text('RECOMMENDED STARTING POINT', margin + 16, y + 16);

            // Group badge
            doc.setFillColor(...COLOR_PRIMARY);
            doc.roundedRect(margin + 16, y + 22, 52, 16, 3, 3, 'F');
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(9);
            doc.setTextColor(255, 255, 255);
            doc.text(`Group ${recommendedGroup.value}`, margin + 42, y + 33, { align: 'center' });

            // Title
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(13);
            doc.setTextColor(...COLOR_TEXT);
            doc.text(recommendedGroupTitle.value, margin + 76, y + 34);

            // Metrics line
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(...COLOR_MUTED);
            const metrics = [
                `Max Syllables: ${maxMetrics.value.syllable || '—'}`,
                `Max Consonants: ${maxMetrics.value.consonant || '—'}`,
                `Max Vowels: ${maxMetrics.value.vowel || '—'}`,
            ];
            doc.text(metrics.join('   •   '), margin + 16, y + 60);

            y += heroH + 16;
        }

        // === Preferences ===
        const prefs = preferences.value;
        const prefEntries = [
            ['Activities', prefs.activities],
            ['Items', prefs.items],
            ['People & Places', prefs.places],
        ].filter(([, v]) => v && v.trim());

        if (prefEntries.length) {
            ensureSpace(30 + prefEntries.length * 18);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(...COLOR_TEXT);
            doc.text('Learner Preferences', margin, y);
            y += 6;
            doc.setDrawColor(...COLOR_LINE);
            doc.setLineWidth(0.5);
            doc.line(margin, y, pageW - margin, y);
            y += 10;

            for (const [label, value] of prefEntries) {
                ensureSpace(18);
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8);
                doc.setTextColor(...COLOR_PRIMARY);
                doc.text(label.toUpperCase(), margin, y);
                doc.setFont('helvetica', 'normal');
                doc.setFontSize(10);
                doc.setTextColor(...COLOR_TEXT);
                const lines = doc.splitTextToSize(value, contentW - 100);
                doc.text(lines, margin + 100, y);
                y += Math.max(14, lines.length * 12);
            }
            y += 10;
        }

        // === No echoic targets indicated ===
        if (noTargets.value) {
            ensureSpace(24);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(...COLOR_TEXT);
            doc.text(NO_TARGETS_HEADING, margin, y);
            y += 6;
            doc.setDrawColor(...COLOR_LINE);
            doc.setLineWidth(0.5);
            doc.line(margin, y, pageW - margin, y);
            y += 14;

            doc.setFont('helvetica', 'normal');
            doc.setFontSize(10);
            doc.setTextColor(...COLOR_TEXT);
            for (const line of noTargetsPdfLines()) {
                const wrapped = doc.splitTextToSize(line, contentW - 10);
                ensureSpace(wrapped.length * 12 + 6);
                doc.text(wrapped, margin, y);
                y += wrapped.length * 12 + 4;
            }
            y += 10;
        }

        // === Targets ===
        if (hasTargets.value) {
            ensureSpace(24);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(12);
            doc.setTextColor(...COLOR_TEXT);
            doc.text(`Shaping Targets (${validTargets.value.length})`, margin, y);
            y += 6;
            doc.setDrawColor(...COLOR_LINE);
            doc.setLineWidth(0.5);
            doc.line(margin, y, pageW - margin, y);
            y += 14;

            for (let tIdx = 0; tIdx < validTargets.value.length; tIdx++) {
                const target = validTargets.value[tIdx];
                const cardH = 28 + target.inputs.length * 20 + 10;
                ensureSpace(cardH + 8);

                // Card border
                doc.setDrawColor(...COLOR_LINE);
                doc.setLineWidth(0.8);
                doc.roundedRect(margin, y, contentW, cardH, 4, 4, 'S');

                // Head row
                // Badge
                doc.setFillColor(...COLOR_PRIMARY);
                doc.roundedRect(margin + 10, y + 8, 28, 14, 2, 2, 'F');
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(8);
                doc.setTextColor(255, 255, 255);
                doc.text(`T${tIdx + 1}`, margin + 24, y + 18, { align: 'center' });

                // Word
                doc.setFont('helvetica', 'bold');
                doc.setFontSize(13);
                doc.setTextColor(...COLOR_TEXT);
                doc.text(target.header, margin + 46, y + 19);

                // Divider under head
                doc.setDrawColor(...COLOR_LINE);
                doc.setLineWidth(0.3);
                doc.line(margin + 10, y + 28, margin + contentW - 10, y + 28);

                // Steps
                let stepY = y + 38;
                for (const input of target.inputs) {
                    const kind = Object.keys(input)[0];
                    const label = stepLabel(input);
                    const value = stepValue(input);

                    // Left accent
                    let accent = COLOR_LINE;
                    if (kind === 'end') accent = COLOR_SUCCESS;
                    else if (kind === 'start') accent = COLOR_PRIMARY;
                    doc.setFillColor(...accent);
                    doc.rect(margin + 14, stepY - 10, 3, 14, 'F');

                    // Label pill
                    doc.setFont('helvetica', 'bold');
                    doc.setFontSize(7);
                    let labelColor = COLOR_MUTED;
                    if (kind === 'end') labelColor = COLOR_SUCCESS;
                    else if (kind === 'start') labelColor = COLOR_PRIMARY;
                    doc.setTextColor(...labelColor);
                    doc.text(label.toUpperCase(), margin + 22, stepY);

                    // Value
                    doc.setFont('helvetica', 'normal');
                    doc.setFontSize(11);
                    doc.setTextColor(...COLOR_TEXT);
                    doc.text(value, margin + 70, stepY);

                    stepY += 18;
                }

                y += cardH + 8;
            }
        }

        // === Session Notes ===
        y += 6;
        ensureSpace(24 + 8 * 16);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(...COLOR_TEXT);
        doc.text('Session Notes', margin, y);
        y += 6;
        doc.setDrawColor(...COLOR_LINE);
        doc.setLineWidth(0.5);
        doc.line(margin, y, pageW - margin, y);
        y += 16;

        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.4);
        for (let i = 0; i < 8; i++) {
            doc.line(margin, y, pageW - margin, y);
            y += 18;
        }

        // === Footer on every page ===
        const pageCount = doc.internal.getNumberOfPages();
        for (let p = 1; p <= pageCount; p++) {
            doc.setPage(p);
            doc.setFont('helvetica', 'normal');
            doc.setFontSize(8);
            doc.setTextColor(...COLOR_MUTED);
            doc.text('Generated via EESAPP Guided Plan', margin, pageH - 20);
            doc.text(`Page ${p} of ${pageCount}   •   ${today}`, pageW - margin, pageH - 20, { align: 'right' });
        }

        doc.save(safeFilename());
    } catch (e) {
        console.error('[PDF] Export failed', e);
    } finally {
        exporting.value = false;
    }
};
</script>

<style scoped>
.targets-pdf {
    display: inline-flex;
    width: 100%;
}

.targets-pdf .btn {
    width: 100%;
}

.btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 18px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: background 0.15s;
}

.btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.btn--secondary {
    background: #fff;
    color: #725CFF;
    border: 1px solid #e4dfff;
}

.btn--secondary:not(:disabled):hover {
    background: #f7f6ff;
}

.btn .material-symbols-outlined {
    font-size: 18px;
}
</style>
